import { useState, useEffect, useCallback } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card'
import { Button } from '../ui/Button'
import { RefreshCw } from 'lucide-react'
import salesService from '../../services/salesService'
import { parseDBDate } from '../../utils/timezone'

const SalesTrendChart = ({ className = '' }) => {
  const [salesTrendLoading, setSalesTrendLoading] = useState(false)
  const [salesTrendPeriod, setSalesTrendPeriod] = useState('week') // 'today', 'week', 'month'
  const [salesTrend, setSalesTrend] = useState([])

  const formatNumber = useCallback((num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M'
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K'
    }
    return num.toString()
  }, [])

  const loadSalesTrend = useCallback(async (period = 'week') => {
    try {
      setSalesTrendLoading(true)
      console.log('📈 Loading sales trend data for period:', period)
      
      // Calculate date ranges based on period (already in Sri Lanka time from DB)
      const endDate = new Date()
      const startDate = new Date()
      
      switch (period) {
        case 'today':
          startDate.setHours(0, 0, 0, 0)
          break
        case 'week':
          startDate.setDate(startDate.getDate() - 7)
          break
        case 'month':
          startDate.setDate(startDate.getDate() - 30)
          break
        default:
          startDate.setDate(startDate.getDate() - 7)
      }
      
      const response = await salesService.getSalesByDateRange(
        startDate.toISOString().split('T')[0],
        endDate.toISOString().split('T')[0]
      )
      
      if (response.success && response.data?.orders) {
        const orders = response.data.orders
        console.log('✅ Sales trend raw data:', orders.length, 'orders')
        
        // Group sales by date
        const salesByDate = {}
        const dateKeys = []
        
        // Generate date keys based on period
        if (period === 'today') {
          // Group by hours for today
          for (let i = 0; i < 24; i++) {
            const hour = i.toString().padStart(2, '0') + ':00'
            dateKeys.push(hour)
            salesByDate[hour] = 0
          }
        } else {
          // Group by days for week/month
          const days = period === 'week' ? 7 : 30
          for (let i = days - 1; i >= 0; i--) {
            const date = new Date()
            date.setDate(date.getDate() - i)
            const displayKey = period === 'week' 
              ? date.toLocaleDateString('en-US', { weekday: 'short' })
              : date.getDate().toString()
            dateKeys.push(displayKey)
            salesByDate[displayKey] = 0
          }
        }
        
        // Process orders and group by date/time
        orders.forEach(order => {
          // Parse date from DB (handles Sri Lanka time correctly)
          const orderDate = parseDBDate(order.orderDate || order.createdAt)
          let key
          
          if (period === 'today') {
            key = orderDate.getHours().toString().padStart(2, '0') + ':00'
          } else {
            if (period === 'week') {
              key = orderDate.toLocaleDateString('en-US', { weekday: 'short' })
            } else {
              key = orderDate.getDate().toString()
            }
          }
          
          if (Object.prototype.hasOwnProperty.call(salesByDate, key)) {
            const orderTotal = parseFloat(order.orderPayment?.totalAmount || order.totalAmount || order.total || 0)
            console.log(`📊 Adding order ${order.orderNumber}: Rs.${orderTotal} to ${key}`)
            salesByDate[key] += orderTotal
          }
        })
        
        // Convert to chart data format
        const chartData = dateKeys.map(key => ({
          label: key,
          value: salesByDate[key] || 0,
          displayValue: `Rs.${formatNumber(salesByDate[key] || 0)}`
        }))
        
        setSalesTrend(chartData)
        console.log('✅ Sales trend processed:', chartData)
      } else {
        console.warn('⚠️ No sales data received')
        setSalesTrend([])
      }
    } catch (error) {
      console.error('❌ Error loading sales trend:', error)
      // Set sample data on error
      const sampleData = [
        { label: 'Mon', value: 15000, displayValue: 'Rs.15K' },
        { label: 'Tue', value: 22000, displayValue: 'Rs.22K' },
        { label: 'Wed', value: 18000, displayValue: 'Rs.18K' },
        { label: 'Thu', value: 26000, displayValue: 'Rs.26K' },
        { label: 'Fri', value: 35000, displayValue: 'Rs.35K' },
        { label: 'Sat', value: 42000, displayValue: 'Rs.42K' },
        { label: 'Sun', value: 28000, displayValue: 'Rs.28K' }
      ]
      setSalesTrend(sampleData)
    } finally {
      setSalesTrendLoading(false)
    }
  }, [formatNumber])

  useEffect(() => {
    console.log('🔄 useEffect triggered with period:', salesTrendPeriod)
    loadSalesTrend(salesTrendPeriod)
  }, [loadSalesTrend, salesTrendPeriod])

  const handlePeriodChange = (newPeriod) => {
    setSalesTrendPeriod(newPeriod)
  }

  const handleRefresh = () => {
    loadSalesTrend(salesTrendPeriod)
  }

  return (
    <Card className={className}>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Sales Trend</CardTitle>
        <div className="flex items-center space-x-2">
          <select 
            className="text-sm border rounded px-2 py-1"
            value={salesTrendPeriod}
            onChange={(e) => handlePeriodChange(e.target.value)}
            disabled={salesTrendLoading}
          >
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
          </select>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleRefresh}
            disabled={salesTrendLoading}
          >
            <RefreshCw className={`h-4 w-4 ${salesTrendLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {salesTrendLoading ? (
          <div className="h-64 bg-gray-50 rounded-lg flex items-center justify-center">
            <div className="text-center">
              <RefreshCw className="h-8 w-8 text-gray-400 animate-spin mx-auto mb-2" />
              <p className="text-gray-500">Loading sales trend...</p>
            </div>
          </div>
        ) : (
          <div className="h-64 p-4">
            {/* Simple Line Chart */}
            <div className="h-full flex flex-col">
              {/* Chart Area */}
              <div className="flex-1 flex items-end justify-between px-2 pb-4 relative">
                {/* Background grid lines */}
                <div className="absolute inset-0 flex flex-col justify-between opacity-20">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="h-px bg-gray-300"></div>
                  ))}
                </div>
                
                {/* Data points and lines */}
                {salesTrend.length > 0 && (
                  <svg className="absolute inset-0 w-full h-full" style={{ zIndex: 1 }}>
                    {/* Draw connecting lines */}
                    {salesTrend.map((point, index) => {
                      if (index === salesTrend.length - 1) return null
                      const nextPoint = salesTrend[index + 1]
                      const maxValue = Math.max(...salesTrend.map(p => p.value))
                      const x1 = (index / (salesTrend.length - 1)) * 100
                      const x2 = ((index + 1) / (salesTrend.length - 1)) * 100
                      const y1 = 100 - ((point.value / (maxValue || 1)) * 80)
                      const y2 = 100 - ((nextPoint.value / (maxValue || 1)) * 80)
                      
                      return (
                        <line
                          key={index}
                          x1={`${x1}%`}
                          y1={`${y1}%`}
                          x2={`${x2}%`}
                          y2={`${y2}%`}
                          stroke="#3b82f6"
                          strokeWidth="2"
                          fill="none"
                        />
                      )
                    })}
                    
                    {/* Draw data points */}
                    {salesTrend.map((point, index) => {
                      const maxValue = Math.max(...salesTrend.map(p => p.value))
                      const x = (index / (salesTrend.length - 1)) * 100
                      const y = 100 - ((point.value / (maxValue || 1)) * 80)
                      
                      return (
                        <g key={index}>
                          <circle
                            cx={`${x}%`}
                            cy={`${y}%`}
                            r="4"
                            fill="#3b82f6"
                            stroke="white"
                            strokeWidth="2"
                          />
                          {/* Tooltip on hover */}
                          <title>{`${point.label}: ${point.displayValue}`}</title>
                        </g>
                      )
                    })}
                  </svg>
                )}
              </div>
              
              {/* X-axis labels */}
              <div className="flex justify-between text-xs text-gray-600 px-2">
                {salesTrend.map((point, index) => (
                  <span key={index} className="text-center">
                    {point.label}
                  </span>
                ))}
              </div>
              
              {/* Summary */}
              <div className="mt-4 flex justify-between items-center text-sm">
                <span className="text-gray-600">
                  Period: {salesTrendPeriod === 'today' ? 'Today (Hourly)' : 
                           salesTrendPeriod === 'week' ? 'Last 7 Days' : 'Last 30 Days'}
                </span>
                <span className="font-medium text-blue-600">
                  Total: Rs.{formatNumber(salesTrend.reduce((sum, point) => sum + point.value, 0))}
                </span>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default SalesTrendChart